import { useMutation, useQuery } from '@apollo/client/react';
import {
  faCheckCircle,
  faCircleCheck,
  faTimesCircle,
  faWarning,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import capitalize from 'lodash/capitalize';
import times from 'lodash/times';
import React, { useMemo, useState } from 'react';
import { Control, Controller, FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router';
import styled from 'styled-components';
import {
  AssignTrainingPlanDocument,
  MemberDocument,
  SelfDocument,
  TrainingPlanDetailsFragment,
  TrainingPlanTemplateDocument,
  UpdateTrainingPlanDocument,
  UpdateTrainingPlanInput,
} from '../../../graphql/types';
import { useQueryParams } from '../../../hooks/useQueryParams';
import useTracking from '../../../hooks/useTracking';
import {
  EventCategories,
  EventLabels,
  EventNames,
  ScreenNames,
} from '../../../utils/trackingEvents';
import LoadingPanel from '../../LoadingPanel';
import Button, { ButtonVariant } from '../../reusable/Button';
import GenericError from '../../reusable/GenericError';
import { FeatureHeader } from '../../reusable/Header/FeatureHeader';
import Input, { InputSize } from '../../reusable/Input';
import NavigationBlockerModal from '../../reusable/NavigationBlockerModal/NavigationBlockerModal';
import { SnackbarVariant } from '../../reusable/Snackbar';
import { useSnackbarContext } from '../../reusable/Snackbar/SnackbarContextProvider';
import { Tab, TabPanel, Tabs } from '../../reusable/Tabs';
import Text, { TextColor, TextKind } from '../../reusable/Text';
import ConfirmArchivePlan from './ConfirmArchivePlan';
import { TrainingPlanWeekView } from './TrainingPlanWeekView';
import { useTrainingPlanAttributeLabels } from './useTrainingPlanAttributeLabels';
import {
  convertTemplateToPlanInput,
  getPlanIndicator,
  getTrainingPlanSummary,
  toDuplicatedPlan,
} from './util';

export const StyledPage = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 100%;
`;

export const StyledMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding:  24px;
  padding-bottom: 64px;
`;

interface CustomizeTrainingPlanTemplateProps {}

const getDefaultPlanName = (
  isAssigned: boolean,
  assignedName: string | null | undefined,
  memberFirstName: string | null | undefined,
  typeName: string | null | undefined,
  phase: number | null | undefined,
): string => {
  if (isAssigned) {
    return assignedName ?? '';
  }
  const planDescription = [
    typeName ?? null,
    'Training Plan',
    phase != null ? `Phase ${phase}` : null,
  ]
    .filter(Boolean)
    .join(' ');
  return memberFirstName
    ? `${capitalize(memberFirstName)}'s ${planDescription}`
    : `Your ${planDescription}`;
};

type TrainingPlanFormValues = {
  trainingPlan: TrainingPlanDetailsFragment | null | undefined;
  planName: string;
};

export const CustomizeTrainingPlanTemplate: React.FC<
  CustomizeTrainingPlanTemplateProps
> = () => {
  const { memberId, planId } = useParams();
  const { track } = useTracking();
  const navigate = useNavigate();
  const { setSnackbar } = useSnackbarContext();
  const { pathname } = useLocation();
  const { queryParams } = useQueryParams<{ duplicate?: boolean }>(pathname);
  const isDuplicating = Boolean(queryParams.duplicate);
  const { data: selfData } = useQuery(SelfDocument);
  const {
    data: trainingPlanTemplateData,
    loading: trainingPlanTemplateLoading,
    error: trainingPlanTemplateError,
  } = useQuery(TrainingPlanTemplateDocument, {
    variables: {
      id: planId!,
      memberId: memberId!,
    },
    skip: !planId || !memberId,
  });

  const { data: memberData } = useQuery(MemberDocument, {
    variables: {
      id: memberId ?? '',
    },
    skip: !memberId,
  });

  const labels = useTrainingPlanAttributeLabels();

  const queriedTrainingPlan = trainingPlanTemplateData?.trainingPlan;
  const initialTrainingPlan = useMemo(
    () =>
      isDuplicating && queriedTrainingPlan
        ? toDuplicatedPlan(queriedTrainingPlan)
        : queriedTrainingPlan,
    [isDuplicating, queriedTrainingPlan],
  );

  const methods = useForm<TrainingPlanFormValues>({
    mode: 'onBlur',
    values: {
      trainingPlan: initialTrainingPlan,
      planName: getDefaultPlanName(
        !!trainingPlanTemplateData?.trainingPlan?.user?.id,
        trainingPlanTemplateData?.trainingPlan?.name,
        memberData?.user?.firstName,
        trainingPlanTemplateData?.trainingPlan?.type?.name,
        trainingPlanTemplateData?.trainingPlan?.phase,
      ),
    },
    // Prevent async query re-syncs from overwriting values the user has already edited.
    resetOptions: {
      keepDirtyValues: true,
    },
  });
  const formTrainingPlan = methods.getValues().trainingPlan;
  const [assignTrainingPlanMutation, { loading: assignPlanLoading }] =
    useMutation(AssignTrainingPlanDocument);
  const [activeTab, setActiveTab] = useState(0);
  const [archiveConfirmationOpen, setArchiveConfirmationOpen] = useState(false);

  const defaultTrackingData = React.useMemo(
    () => ({
      event_category: EventCategories.TrainingPlans,
      training_plan_id: formTrainingPlan?.id,
    }),
    [formTrainingPlan?.id],
  );

  const handleAssignTrainingPlanClick = async (
    values: TrainingPlanFormValues,
  ) => {
    if (!memberId || !selfData?.self?.id || !values.trainingPlan || !planId) {
      return;
    }
    const templateId = values.trainingPlan.user?.id
      ? values.trainingPlan.template?.id
      : planId;
    if (!templateId) {
      setSnackbar({
        message: 'This plan has no template and cannot be duplicated.',
        icon: faWarning,
        variant: SnackbarVariant.Danger,
      });
      return;
    }
    await assignTrainingPlanMutation({
      variables: {
        input: convertTemplateToPlanInput(values.trainingPlan, {
          coachId: selfData.self.id,
          userId: memberId,
          templateId,
          name: values.planName,
        }),
      },
      onCompleted: () => {
        methods.reset(methods.getValues()); // reset dirty state to prevent blocker
        navigate(`/app/members/${memberId}?activeTab=trainingPlans`, {
          replace: true,
        });
        setSnackbar({
          message: 'Training plan created.',
          icon: faCircleCheck,
          variant: SnackbarVariant.Success,
        });
      },
      onError: () => {
        setSnackbar({
          message: 'Failed to create training plan.',
          icon: faWarning,
          variant: SnackbarVariant.Danger,
        });
      },
    });
  };

  const [updateTrainingPlan, { loading: updatePlanLoading }] = useMutation(
    UpdateTrainingPlanDocument,
  );
  const handleUpdateTrainingPlan = async ({
    trainingPlan: tp,
    planName,
  }: TrainingPlanFormValues) => {
    if (!tp) {
      return;
    }

    const updatedPlanInput: UpdateTrainingPlanInput = {
      id: tp.id,
      name: planName,
      weeks: tp.weeks.edges.map(({ node: week }) => {
        return {
          order: week.order,
          workouts: week.workouts.edges
            .map(({ node }) => node)
            .filter((workout) => {
              const workoutCompleted = workout.completedAt;
              const hasCompletedExercises = workout.blocks.edges.some(
                ({ node: block }) =>
                  block.sets.edges.some(({ node: set }) =>
                    set.trainingPlanExercises.edges.some(
                      ({ node: exercise }) => exercise.completedAt,
                    ),
                  ),
              );

              return !hasCompletedExercises && !workoutCompleted;
            })
            .map((workout) => ({
              id: workout.id,
              order: workout.order,
              blocks: workout.blocks.edges.map(({ node: block }) => ({
                order: block.order,
                trainingComponent: block.trainingComponent.value,
                sets: times(block.sets.count, (index) => ({
                  order: index,
                  trainingPlanExercises:
                    block.sets.edges[0].node.trainingPlanExercises.edges.map(
                      ({ node: tpExercise }) => ({
                        exerciseId: tpExercise.exercise.id,
                        count: parseInt(tpExercise.count.toString()),
                        order: tpExercise.order,
                        unit: tpExercise.unit.value,
                      }),
                    ),
                })),
              })),
            })),
        };
      }),
    };

    await updateTrainingPlan({
      variables: {
        input: updatedPlanInput,
      },
      onCompleted: () => {
        setSnackbar({
          message: 'Training plan updated.',
          variant: SnackbarVariant.Success,
          icon: faCheckCircle,
        });
      },
      onError: () => {
        setSnackbar({
          message: 'Failed to update training plan, try again later.',
          variant: SnackbarVariant.Danger,
          icon: faTimesCircle,
        });
      },
    });
  };

  const onNavigationTabChange = (tab: number) => {
    setActiveTab(tab);
    track(EventNames.NavTabClick, {
      event_category: EventCategories.Navigation,
      event_label: `Week ${tab + 1}`,
    });
  };

  const isSettingUpForm = Boolean(
    trainingPlanTemplateData?.trainingPlan && !formTrainingPlan,
  );

  if (trainingPlanTemplateLoading || isSettingUpForm) {
    return (
      <>
        <FeatureHeader
          state="loading"
          name="training plan"
          trackingData={defaultTrackingData}
        />
        <LoadingPanel noBorder height={500} />
      </>
    );
  }

  if (trainingPlanTemplateError || !formTrainingPlan) {
    return (
      <>
        <FeatureHeader
          state="loading"
          name="training plan"
          trackingData={defaultTrackingData}
        />
        <GenericError
          testId="CustomizeTrainingPlanTemplate-Error"
          error={trainingPlanTemplateError}
        />
      </>
    );
  }

  const isTemplate = !formTrainingPlan.user?.id;
  const isCreating = isTemplate || isDuplicating;
  const isCompleted = !!formTrainingPlan.completedAt;
  const isArchived = !!formTrainingPlan.archivedAt;
  const canEdit = !isCompleted && !isArchived;
  const isCurrentPlan =
    trainingPlanTemplateData?.user?.activeTrainingPlan?.edges[0]?.node?.id ===
    formTrainingPlan.id;
  const { indicatorIcon, indicatorColor, indicatorText, dateText } =
    getPlanIndicator(formTrainingPlan, isCurrentPlan);

  let state: 'create' | 'edit' | 'view' = 'view';
  if (canEdit && !isTemplate) {
    state = 'edit';
  }
  if (isCreating) {
    state = 'create';
  }

  return (
    <FormProvider {...methods}>
      <StyledPage>
        <FeatureHeader
          state={state}
          name="training plan"
          trackingData={defaultTrackingData}
        />
        <StyledMain>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {isTemplate || canEdit ? (
                <div style={{ maxWidth: 400 }}>
                  {/*
                    TrainingPlanDetailsFragment is deeply nested (plan → weeks → workouts →
                    blocks → sets → exercises), which causes TS to hit its instantiation depth
                    limit (TS2589) when computing Path<TrainingPlanFormValues> for the name prop.
                    Narrowing control to only planName prevents that traversal.
                  */}
                  <Controller
                    name="planName"
                    control={
                      methods.control as unknown as Control<
                        Pick<TrainingPlanFormValues, 'planName'>
                      >
                    }
                    rules={{ required: 'Plan name is required' }}
                    render={({ field }) => (
                      <>
                        <Input
                          id="trainingPlan-input-name"
                          label="Plan name"
                          maxLength={50}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          ref={field.ref}
                          name={field.name}
                          value={field.value}
                          size={InputSize.Small}
                          trackingData={{
                            ...defaultTrackingData,
                            event_label: 'Plan Name',
                          }}
                        />
                        <Text kind={TextKind.BodyS} color={TextColor.Secondary}>
                          {field.value?.length ?? 0}/50
                        </Text>
                      </>
                    )}
                  />
                </div>
              ) : (
                <Text kind={TextKind.TitleL} component="h1">
                  {formTrainingPlan.name}
                </Text>
              )}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 4,
                  marginBottom: 16,
                }}
                data-testid="trainingPlan-basedOn"
              >
                <Text kind={TextKind.SubtitleXs}>Based on template:</Text>
                <Text kind={TextKind.SubtitleXs} bold>
                  Name:{' '}
                  {isTemplate
                    ? formTrainingPlan.name
                    : (formTrainingPlan.template?.name ?? 'unknown')}
                </Text>
                {(formTrainingPlan.template || formTrainingPlan) && (
                  <Text
                    kind={TextKind.SubtitleXs}
                    bold
                    testId="trainingPlan-attributes"
                  >
                    Attributes:{' '}
                    {getTrainingPlanSummary(
                      formTrainingPlan.template || formTrainingPlan,
                      labels,
                    )}
                  </Text>
                )}
              </div>
            </div>
            {isCreating && (
              <Button
                variant={ButtonVariant.Dark}
                onClick={methods.handleSubmit(handleAssignTrainingPlanClick)}
                loading={assignPlanLoading}
                trackingData={{
                  ...defaultTrackingData,
                  event_label: EventLabels.Publish,
                }}
                disabled={
                  Object.keys(methods.formState.errors).length > 0 ||
                  assignPlanLoading
                }
              >
                Publish
              </Button>
            )}
            {!isCreating && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  flexDirection: 'column',
                  gap: 24,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <Button
                    variant={ButtonVariant.Outline}
                    onClick={() =>
                      navigate(
                        `/app/members/${memberId}/trainingPlan/${planId}/?duplicate=true`,
                      )
                    }
                    trackingData={{
                      ...defaultTrackingData,
                      event_label: EventLabels.Duplicate,
                    }}
                  >
                    Duplicate
                  </Button>
                  {!isArchived && !isCompleted && (
                    <Button
                      variant={ButtonVariant.Outline}
                      onClick={() => setArchiveConfirmationOpen(true)}
                      trackingData={{
                        ...defaultTrackingData,
                        event_label: EventLabels.Archive,
                      }}
                    >
                      Archive
                    </Button>
                  )}
                  {canEdit && !isTemplate && (
                    <Button
                      variant={ButtonVariant.Dark}
                      onClick={methods.handleSubmit(handleUpdateTrainingPlan)}
                      loading={updatePlanLoading}
                      disabled={
                        Object.keys(methods.formState.errors).length > 0 ||
                        updatePlanLoading ||
                        !methods.formState.isDirty
                      }
                      trackingData={{
                        ...defaultTrackingData,
                        event_label: EventLabels.PublishChanges,
                      }}
                    >
                      Publish Changes
                    </Button>
                  )}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '11px',
                  }}
                  data-testid="trainingPlan-status"
                >
                  <FontAwesomeIcon
                    icon={indicatorIcon}
                    color={indicatorColor}
                    fontSize={16}
                  />
                  <Text kind={TextKind.SubtitleXs}>
                    {!isArchived && !isCompleted && `${indicatorText} •`}{' '}
                    {dateText}
                  </Text>
                </div>
              </div>
            )}
          </div>

          <Tabs
            selectionFollowsFocus
            value={activeTab}
            onChange={(_, newValue) => onNavigationTabChange(newValue)}
          >
            {formTrainingPlan.weeks.edges.map((week) => (
              <Tab
                key={week.node.id}
                index={week.node.order}
                label={`Week ${week.node.order + 1}`}
                tabs="trainingPlanWeeks"
              />
            ))}
          </Tabs>

          {formTrainingPlan.weeks.edges.map(({ node: week }) => (
            <TabPanel
              tabs="trainingPlanWeeks"
              value={activeTab}
              index={week.order}
              key={week.id}
            >
              <TrainingPlanWeekView
                canEdit={canEdit}
                week={week}
                trackingData={defaultTrackingData}
              />
            </TabPanel>
          ))}
        </StyledMain>
      </StyledPage>
      <ConfirmArchivePlan
        isOpen={archiveConfirmationOpen}
        onClose={() => setArchiveConfirmationOpen(false)}
        onConfirm={() => {
          setArchiveConfirmationOpen(false);
          navigate(
            `/app/members/${memberData?.user?.id}?activeTab=trainingPlans`,
          );
        }}
        trainingPlan={formTrainingPlan}
      />
      <NavigationBlockerModal
        description={
          'The changes you made to the training plan will not be saved. This action cannot be undone.'
        }
        when={methods.formState.isDirty}
        trackingData={{
          ...defaultTrackingData,
          screen_name: ScreenNames.CustomizeTrainingPlan,
        }}
      />
    </FormProvider>
  );
};
